import { Injectable } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { DeviceType } from '../types/common/device.types';
import { AppConfigService } from './config.service';

@Injectable({
  providedIn: 'root',
})
export class WindowLocationService {
  constructor(private appConfigService: AppConfigService) {}

  openMaps(query: string): void {
    window.location.href = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(query);
  }

  openStore(): void {
    switch (Capacitor.getPlatform()) {
      case DeviceType.Android: {
        window.location.href = this.appConfigService.playStoreLink;
        break;
      }
      case DeviceType.IOS: {
        window.location.href = this.appConfigService.appStoreLink;
        break;
      }
    }
  }

  openCalendarSubscription(webcalUrl: string, googleCalendarUrl: string): void {
    if (Capacitor.getPlatform() === DeviceType.IOS) {
      window.location.href = webcalUrl;
      return;
    }
    Browser.open({ url: googleCalendarUrl });
  }

  mailTo(email: string): void {
    if (!email) {
      return;
    }
    window.location.href = `mailto:${email}`;
  }

  phoneCall(phone: string): void {
    if (!phone) {
      return;
    }
    window.location.href = `tel:${phone}`;
  }

  smsTo(phone: string, smsContent?: string): void {
    if (!phone) {
      return;
    }
    window.location.href = `sms:${phone};?&body=${smsContent ?? ''}`;
  }

  /**
   * Opens a WhatsApp chat with `whatsAppNumber`, which must already be in the
   * digits-only international form `wa.me` expects (see `toWhatsAppNumber`).
   *
   * Uses the `https://wa.me/` link rather than the `whatsapp://` scheme on
   * purpose: it is an iOS universal link and a verified Android app link, so the
   * OS opens the WhatsApp app when it is installed and falls back to the wa.me
   * install page in the browser when it is not. The custom scheme is a silent,
   * undetectable no-op when WhatsApp is missing.
   *
   * For the same reason `wa.me` must never be added to `allowNavigation` in
   * `capacitor.config.ts` — that would navigate the in-app webview to wa.me and
   * strand the user there instead of handing off to the OS.
   */
  whatsAppTo(whatsAppNumber: string, whatsAppContent?: string): void {
    if (!whatsAppNumber) {
      return;
    }
    const text = whatsAppContent?.trim();
    const query = text ? `?text=${encodeURIComponent(text)}` : '';

    window.location.href = `https://wa.me/${whatsAppNumber}${query}`;
  }
}
